import { useMemo, useState } from "react"
import { Link, router } from "@inertiajs/react"
import { Button } from "@/components/ui/button.tsx"
import type { CommunityType } from "@/pages/communities/types.ts"
import { Input } from "@/components/ui/input.tsx"
import Pagination from "@/components/pagination.tsx"
import { Building2, DollarSign, Edit, Eye, MoreVertical, Plus, Trash2, Users } from "lucide-react"
import { Card } from "@/components/ui/card.tsx"
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger
} from "@/components/ui/dropdown-menu.tsx"
import { Badge } from "@/components/ui/badge.tsx"
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow
} from "@/components/ui/table.tsx"

interface IndexProps {
    communities: CommunityType[]
    permissions: {
        can_create: boolean
        can_edit: boolean
        can_delete: boolean
    }
    pagination: {
        current_page: number
        total_pages: number
        total_count: number
        per_page: number
        next_page: number | null
        prev_page: number | null
    }
}

const Index = ({ communities, pagination, permissions }: IndexProps) => {
    const [searchTerm, setSearchTerm] = useState("")
    const [statusFilter, setStatusFilter] = useState<"all" | "active" | "inactive">("all")

    const filteredCommunities = useMemo(() => {
        const term = searchTerm.trim().toLowerCase()

        return communities.filter(community => {
            const matchesStatus = statusFilter === "all" || community.status === statusFilter
            const matchesTerm = term.length === 0
                || community.name.toLowerCase().includes(term)
                || community.address.toLowerCase().includes(term)

            return matchesStatus && matchesTerm
        })
    }, [communities, searchTerm, statusFilter])

    const handleDestroy = (community: CommunityType) => {
        if (!window.confirm(`Supprimer la communauté "${community.name}" ?`)) return

        router.delete(`/communities/${community.id}`, {
            preserveScroll: true,
        })
    }

    return (
        <div className="flex flex-col gap-6 p-8">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-3xl font-bold text-foreground">Communautés</h1>
                    <p className="text-sm text-muted-foreground">Gestion des communes</p>
                </div>
                {permissions.can_create && (
                    <Link href="/communities/new">
                        <Button className="gap-2">
                            <Plus className="h-4 w-4" />
                            Ajouter une communauté
                        </Button>
                    </Link>
                )}
            </div>

            {/* Stats Overview */}
            <div className="grid gap-4 md:grid-cols-4">
                <Card className="border-border bg-card p-6">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm font-medium text-muted-foreground">Total Communautés</p>
                            <p className="mt-2 text-3xl font-bold text-foreground">{pagination.total_count}</p>
                        </div>
                        <Building2 className="h-8 w-8 text-primary" />
                    </div>
                </Card>

                <Card className="border-border bg-card p-6">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm font-medium text-muted-foreground">Total Population</p>
                            <p className="mt-2 text-3xl font-bold text-foreground">0</p>
                        </div>
                        <Users className="h-8 w-8 text-chart-2" />
                    </div>
                </Card>

                <Card className="border-border bg-card p-6">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm font-medium text-muted-foreground">Total des contribuables</p>
                            <p className="mt-2 text-3xl font-bold text-foreground">0</p>
                        </div>
                        <Users className="h-8 w-8 text-chart-3" />
                    </div>
                </Card>

                <Card className="border-border bg-card p-6">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm font-medium text-muted-foreground">Total collecté</p>
                            <p className="mt-2 text-3xl font-bold text-foreground">${(0 / 1000000).toFixed(2)}M</p>
                        </div>
                        <DollarSign className="h-8 w-8 text-chart-4" />
                    </div>
                </Card>
            </div>

            <Card className="border-border bg-card p-4">
                <div className="grid gap-3 md:grid-cols-3">
                    <div className="md:col-span-2">
                        <Input
                            type="text"
                            placeholder="Rechercher par nom ou adresse"
                            value={searchTerm}
                            onChange={event => setSearchTerm(event.target.value)}
                        />
                    </div>
                    <div>
                        <select
                            className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground"
                            value={statusFilter}
                            onChange={event => setStatusFilter(event.target.value as "all" | "active" | "inactive")}
                        >
                            <option value="all">Tous les statuts</option>
                            <option value="active">Actif</option>
                            <option value="inactive">Inactif</option>
                        </select>
                    </div>
                </div>
            </Card>

            <Card className="p-4">
                <Table>
                    <TableHeader>
                        <TableRow>
                            <TableHead>Logo</TableHead>
                            <TableHead>Code</TableHead>
                            <TableHead>Nom</TableHead>
                            <TableHead>Région</TableHead>
                            <TableHead>Adresse</TableHead>
                            <TableHead>Téléphone</TableHead>
                            <TableHead>Statut</TableHead>
                            <TableHead>Actions</TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {filteredCommunities.length === 0 ? (
                            <TableRow>
                                <TableCell className="text-center text-muted-foreground" colSpan={8}>
                                    Aucune communauté trouvée.
                                </TableCell>
                            </TableRow>
                        ) : (
                            filteredCommunities.map((community) => (
                                <TableRow key={community.id}>
                                    <TableCell>
                                        {community.logo_url ? (
                                            <img
                                                src={community.logo_url}
                                                alt={`Logo ${community.name}`}
                                                className="h-10 w-10 rounded-md border border-border object-contain"
                                            />
                                        ) : (
                                            <div className="flex h-10 w-10 items-center justify-center rounded-md border border-border bg-secondary">
                                                <Building2 className="h-5 w-5 text-muted-foreground" />
                                            </div>
                                        )}
                                    </TableCell>
                                    <TableCell>{community.code}</TableCell>
                                    <TableCell>{community.name}</TableCell>
                                    <TableCell>{community.location}</TableCell>
                                    <TableCell>{community.address}</TableCell>
                                    <TableCell>{community.phone}</TableCell>
                                    <TableCell>
                                        <Badge variant={community.status === "active" ? "default" : "destructive"}>
                                            {community.status}
                                        </Badge>
                                    </TableCell>
                                    <TableCell className="text-right">
                                        <DropdownMenu>
                                            <DropdownMenuTrigger asChild>
                                                <Button variant="ghost" size="icon">
                                                    <MoreVertical className="h-4 w-4" />
                                                </Button>
                                            </DropdownMenuTrigger>
                                            <DropdownMenuContent align="end">
                                                <DropdownMenuItem>
                                                    <Link href={`/communities/${community.id}`} className="flex items-center gap-2 text-foreground">
                                                        <Eye className="mr-2 h-4 w-4" />
                                                        Voir les détails
                                                    </Link>
                                                </DropdownMenuItem>
                                                {permissions.can_edit && (
                                                    <DropdownMenuItem>
                                                        <Link
                                                            href={`/communities/${community.id}/edit`}
                                                            className="flex items-center gap-2 text-foreground"
                                                        >
                                                            <Edit className="mr-2 h-4 w-4" />
                                                            Edit
                                                        </Link>
                                                    </DropdownMenuItem>
                                                )}
                                                {permissions.can_delete && (
                                                    <DropdownMenuItem className="text-destructive" onClick={() => handleDestroy(community)}>
                                                        <Trash2 className="mr-2 h-4 w-4" />
                                                        Supprimer
                                                    </DropdownMenuItem>
                                                )}
                                            </DropdownMenuContent>
                                        </DropdownMenu>
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </Card>

            <Pagination
                currentPage={pagination.current_page}
                totalPages={pagination.total_pages}
                basePath="/communities"
            />
        </div>
    )
}

export default Index
